// Type definitions for Moderator Dashboard

export interface State {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
}

export interface Note {
  id: string;
  content: string;
  order: number;
}

export interface Insurance {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  customerId?: string;
  customer?: Customer;
  createdAt?: string;
  updatedAt?: string;
}

export interface Location {
  id: string;
  name: string;
  city?: string;
  stateId: string;
  state: State;
  customerId?: string;
  customer?: Customer;
  zipCode?: string;
  physicalAddress?: string;
  notes?: Note[];
  isActive?: boolean;
}

export interface ConditionCategory {
  id: string;
  name: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface Condition {
  id: string;
  name: string;
  categoryId?: string;
  category?: ConditionCategory;
  description?: string;
  canCancel: boolean;
  canReschedule: boolean;
  isActive?: boolean;
  notes?: Note[];
  createdAt?: string;
  updatedAt?: string;
}

export interface Customer {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
}

export interface ProviderPhoneNumber {
  id: string;
  name: string;
  phone: string;
  locationId?: string;
  location?: Location;
}

export interface ProviderNote {
  id: string;
  content: string;
  category?: string;
  order: number;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
}

export interface ProviderCondition {
  id: string;
  condition: Condition;
}

export interface ProviderLocation {
  id: string;
  location: Location;
  address?: string;
  suite?: string;
  phone?: string;
}

export interface ProviderInsurance {
  id: string;
  insurance: Insurance;
  notes?: string;
}

export interface Language {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
}

export interface ProviderLanguage {
  id: string;
  languageId: string;
  language: {
    id: string;
    code: string;
    name: string;
  };
}

export interface Provider {
  id: string;
  firstName: string;
  lastName: string;
  title?: string;
  customerId?: string;
  customer?: Customer;
  phoneNumbers?: ProviderPhoneNumber[];
  maLvnName?: string;
  maLvnPhone?: string;
  triageNurseName?: string;
  triageNursePhone?: string;
  surgerySchedulerName?: string;
  surgerySchedulerPhone?: string;
  practiceAdminName?: string;
  practiceAdminPhone?: string;
  minimumAge?: number;
  maximumAge?: number;
  ageRestrictionNotes?: string;
  conditions?: ProviderCondition[];
  locations?: ProviderLocation[];
  insurances?: ProviderInsurance[];
  isActive: boolean;
  acceptsNewPatients?: boolean;
  requiresReferral?: boolean;
  notes?: ProviderNote[];
  npiNumber?: string;
  schedulingInstructions?: string;
  insuranceNotes?: string;
  supervisingProviderId?: string;
  supervisingProvider?: Provider;
  supervisingProviderIds?: string[];
  supervisingProviders?: Provider[];
  apps?: Provider[];
}

export interface AppointmentDetail {
  id: string;
  name: string;
  canCancel: string;
  canReschedule: string;
  canSchedule: string;
  customerId?: string;
  customer?: Customer;
  notes?: Note[];
}

// Form state types
export interface InsuranceFormState {
  name: string;
  description: string;
  isActive: boolean;
  customerId: string;
}

export interface LocationFormState {
  name: string;
  city: string;
  stateId: string;
  customerId: string;
  zipCode: string;
  physicalAddress: string;
  notes: string[];
}

export interface ConditionFormState {
  name: string;
  categoryId: string;
  description: string;
  notes: string[];
}

export interface CustomerFormState {
  name: string;
  description: string;
  isActive: boolean;
}

export interface ProviderFormState {
  title: string;
  firstName: string;
  lastName: string;
  customerId: string;
  specialtyIds: string[];
  languageIds: string[];
  phoneNumbers: Array<{
    id: string;
    name: string;
    phone: string;
    locationId: string;
  }>;
  maLvnName: string;
  maLvnPhone: string;
  triageNurseName: string;
  triageNursePhone: string;
  surgerySchedulerName: string;
  surgerySchedulerPhone: string;
  practiceAdminName: string;
  practiceAdminPhone: string;
  minimumAge: number | null;
  maximumAge: number | null;
  ageRestrictionNotes: string;
  locationIds: string[];
  insuranceIds: string[];
  acceptsNewPatients: boolean;
  requiresReferral: boolean;
  isActive: boolean;
  notes: string[];
  npiNumber: string;
  schedulingInstructions: string;
  insuranceNotes: string;
  supervisingProviderId: string;
  appIds: string[];
}

export interface AppointmentDetailFormState {
  name: string;
  canCancel: string;
  canReschedule: string;
  canSchedule: string;
  customerId: string;
  notes: string[];
}

// Initial state constants
export const initialInsuranceState: InsuranceFormState = {
  name: '',
  description: '',
  isActive: true,
  customerId: ''
};

export const initialLocationState: LocationFormState = {
  name: '',
  city: '',
  stateId: '',
  customerId: '',
  zipCode: '',
  physicalAddress: '',
  notes: []
};

export const initialConditionState: ConditionFormState = {
  name: '',
  categoryId: '',
  description: '',
  notes: []
};

export const initialCustomerState: CustomerFormState = {
  name: '',
  description: '',
  isActive: true
};

export const initialProviderState: ProviderFormState = {
  title: '',
  firstName: '',
  lastName: '',
  customerId: '',
  specialtyIds: [],
  languageIds: [],
  phoneNumbers: [],
  maLvnName: '',
  maLvnPhone: '',
  triageNurseName: '',
  triageNursePhone: '',
  surgerySchedulerName: '',
  surgerySchedulerPhone: '',
  practiceAdminName: '',
  practiceAdminPhone: '',
  minimumAge: null,
  maximumAge: null,
  ageRestrictionNotes: '',
  locationIds: [],
  insuranceIds: [],
  acceptsNewPatients: true,
  requiresReferral: false,
  isActive: true,
  notes: [],
  npiNumber: '',
  schedulingInstructions: '',
  insuranceNotes: '',
  supervisingProviderId: '',
  appIds: []
};

export const initialAppointmentDetailState: AppointmentDetailFormState = {
  name: '',
  canCancel: 'Yes',
  canReschedule: 'Yes',
  canSchedule: 'No',
  customerId: '',
  notes: []
};

